import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Container,
  Form,
  FormField,
  Header,
  Input,
  RadioGroup,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useArbiterClient } from '../../context/ArbiterClientContext';
import { infoPopover } from '../../components/InfoPopover';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { CodeDiffFields } from './CodeDiffFields';
import { CodePackageFields } from './CodePackageFields';
import { CodeReviewFields } from './CodeReviewFields';
import {
  DESCRIPTION_MAX_LENGTH,
  SOURCE,
  buildSubject,
  emptyForm,
  validate,
  type EvaluationFormState,
  type FieldErrors,
  type SubjectType,
} from './evaluationSubjects';

const SUBJECT_TYPE_ITEMS = [
  {
    value: 'codePackage',
    label: 'Code package',
    description: 'Evaluate a whole Brazil package as it stands at one commit.',
  },
  {
    value: 'codeReview',
    label: 'Code review',
    description: 'Evaluate the changes in a CR. Arbiter fetches the diff itself — you only need the CR number.',
  },
  {
    value: 'codeDiff',
    label: 'Code diff',
    description: 'Evaluate a diff you already have, pasted as text or referenced by a pre-signed S3 URL.',
  },
];

const SUBJECT_INFO = infoPopover(
  'Choosing a subject',
  'The subject is what you want Arbiter to check. Code package evaluates an entire package as it stands at one commit. Code review needs only a CR number — Arbiter resolves the diff for you. Code diff is for a diff you already hold. Which rules run is decided by the subject you pick — you never select rules yourself.',
);

const BINDLE_LOCK_INFO = infoPopover(
  'Bindle lock',
  'A Bindle lock is the Amazon permission resource that will own this evaluation, and it controls who can read the results afterwards. It looks like amzn1.bindle.resource.<letters and numbers>. Find yours at bindles.amazon.com, or copy the value your team already uses from an existing evaluation in the Evaluations list.',
);

export function CreateEvaluationPage() {
  useDocumentTitle('Create Evaluation');
  const navigate = useNavigate();
  const client = useArbiterClient();

  const [form, setForm] = useState<EvaluationFormState>(() => emptyForm());
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const patch = <K extends keyof EvaluationFormState>(key: K, value: Partial<EvaluationFormState[K]>) =>
    setForm((prev) => ({ ...prev, [key]: { ...(prev[key] as object), ...value } }));

  // Switching subject clears the subject's own fields, but ownership applies to any subject and
  // is kept — retyping the bindle lock after every switch would be needless.
  const handleSubjectTypeChange = (value: string) => {
    setForm((prev) => ({
      ...emptyForm(value as SubjectType),
      bindleLock: prev.bindleLock,
      description: prev.description,
    }));
    setErrors({});
    setSubmitError(null);
  };

  const handleSubmit = async () => {
    const nextErrors = validate(form);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setSubmitError(null);
    setSubmitting(true);
    try {
      const response = await client.createEvaluation({
        subject: buildSubject(form),
        description: form.description.trim() || undefined,
        bindleLock: form.bindleLock.trim(),
        source: SOURCE,
      });
      navigate(`/evaluations/${response.evaluation?.evaluationId}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to create evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="link" onClick={() => navigate('/evaluations')}>
            Cancel
          </Button>
          <Button variant="primary" loading={submitting} onClick={handleSubmit}>
            Run Evaluation
          </Button>
        </SpaceBetween>
      }
      header={
        <Header
          variant="h1"
          description="Check a code change or package against Arbiter's security rules. Arbiter picks the applicable rules for you and reports a pass or fail for each."
        >
          Create Evaluation
        </Header>
      }
      errorText={submitError}
    >
      <SpaceBetween size="l">
        <Container header={<Header variant="h2" info={SUBJECT_INFO}>What do you want to evaluate?</Header>}>
          <SpaceBetween size="l">
            <RadioGroup
              value={form.subjectType}
              onChange={({ detail }) => handleSubjectTypeChange(detail.value)}
              items={SUBJECT_TYPE_ITEMS}
            />

            {form.subjectType === 'codePackage' && (
              <CodePackageFields
                value={form.codePackage}
                onChange={(next) => patch('codePackage', next)}
                errors={errors}
              />
            )}
            {form.subjectType === 'codeReview' && (
              <CodeReviewFields value={form.codeReview} onChange={(next) => patch('codeReview', next)} errors={errors} />
            )}
            {form.subjectType === 'codeDiff' && (
              <CodeDiffFields value={form.codeDiff} onChange={(next) => patch('codeDiff', next)} errors={errors} />
            )}
          </SpaceBetween>
        </Container>

        <Container header={<Header variant="h2" info={BINDLE_LOCK_INFO}>Ownership</Header>}>
          <SpaceBetween size="l">
            <FormField
              label="Bindle lock"
              description="The Bindle resource that will own this evaluation and control who can read its results."
              constraintText="Required. Format amzn1.bindle.resource.<letters and numbers>."
              errorText={errors.bindleLock}
            >
              <Input
                value={form.bindleLock}
                onChange={({ detail }) => setForm((prev) => ({ ...prev, bindleLock: detail.value }))}
                placeholder="amzn1.bindle.resource.abcdefghijklmnop"
              />
            </FormField>
            <FormField
              label="Description"
              description="A note to your future self about what this evaluation covers. Shown in the evaluations list."
              constraintText={`Optional. Up to ${DESCRIPTION_MAX_LENGTH.toLocaleString()} characters.`}
            >
              <Textarea
                value={form.description}
                onChange={({ detail }) => setForm((prev) => ({ ...prev, description: detail.value }))}
                placeholder="Pre-launch check for the payments API change"
                rows={2}
              />
            </FormField>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </Form>
  );
}
